import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

export interface Feature {
  id: number | string;
  name: string;
  description?: string | null;
  code?: string | null;
  [key: string]: any;
}

export interface PaginatedFeaturesResponse {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: Feature[];
  items?: Feature[];
  // Backend variant: { success, message, result: { data, total, page, page_size } }
  success?: boolean;
  message?: string;
  result?: {
    data?: Feature[];
    total?: number;
    page?: number;
    page_size?: number;
  };
}
const API_BASE_URL = getApiOrigin();
const FETCH_FEATURES_PATH = "/api/v1/features";
const FETCH_USER_PERMISSIONS_PATH = "/api/v1/permissions/user";
export async function fetchFeatures(
  params: { page?: number; pageSize?: number; filters?: Record<string, any> } = {},
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<PaginatedFeaturesResponse> {
    const baseUrl = options?.baseUrlOverride || API_BASE_URL || "";
    if (!baseUrl) {
      throw new Error(
        "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
      );
    }

  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const urlParams = new URLSearchParams();
  urlParams.set("page", String(page));
  urlParams.set("page_size", String(pageSize));

  if (params.filters && Object.keys(params.filters).length > 0) {
    urlParams.set("filters", JSON.stringify(params.filters));
  }

  const url = `${baseUrl}${FETCH_FEATURES_PATH}?${urlParams.toString()}`;

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  const text = await response.text();
  const json = text ? (JSON.parse(text) as PaginatedFeaturesResponse) : null;

  if (!response.ok) {
    const message =
      (json as any)?.message ||
      (json as any)?.detail ||
      `Failed to fetch features (${response.status})`;
    throw new Error(message);
  }

  return json || {};
}

export interface UserPermission {
  id: number | string;
  user_id?: number | string;
  facility_id?: number | string;
  facility_name?: string;
  feature_id?: number | string;
  feature_name?: string;
  view?: boolean;
  add?: boolean;
  update?: boolean;
  delete?: boolean;
  name?: string;
  code?: string;
  description?: string | null;
  [key: string]: any;
}

export interface UserPermissionsResponse {
  success?: boolean;
  permissions?: UserPermission[];
  [key: string]: any;
}

export interface SavePermissionInput {
  feature_id: number | string;
  view: boolean;
  add: boolean;
  update: boolean;
  delete: boolean;
}

export interface SaveUserPermissionsPayload {
  user_id: number | string;
  facility_id?: number | string;
  features: SavePermissionInput[];
}

export interface SaveUserPermissionsResponse {
  success?: boolean;
  message?: string;
  [key: string]: any;
}

/**
 * GET `/api/v1/permissions/user/{userId}` — optional `facility_id` for facility-scoped rows.
 * Omit `facilityId` for user-level (global) permission rows (`facility_id` null in response).
 */
export async function fetchUserPermissionsByUserId(
  userId: number | string,
  options?: {
    baseUrlOverride?: string;
    accessTokenOverride?: string;
    facilityId?: number | string;
  }
): Promise<UserPermissionsResponse> {
  if (userId == null || userId === "") {
    throw new Error("userId is required");
  }

  const baseUrl = options?.baseUrlOverride || API_BASE_URL || "";
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  let url = `${baseUrl}${FETCH_USER_PERMISSIONS_PATH}/${encodeURIComponent(
    String(userId),
  )}`;
  if (
    options?.facilityId != null &&
    options.facilityId !== "" &&
    !Number.isNaN(Number(options.facilityId))
  ) {
    url += `?facility_id=${encodeURIComponent(String(options.facilityId))}`;
  }

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  const text = await response.text();
  const json = text ? (JSON.parse(text) as UserPermissionsResponse) : null;

  if (!response.ok) {
    const message =
      (json as any)?.message ||
      (json as any)?.detail ||
      `Failed to fetch user permissions (${response.status})`;
    throw new Error(message);
  }

  return json || {};
}

export async function fetchUserPermissions(
  userId: number | string,
  facilityId: number | string,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<UserPermissionsResponse> {
  if (userId == null) throw new Error("userId is required");
  if (facilityId == null) throw new Error("facilityId is required");

  return fetchUserPermissionsByUserId(userId, {
    ...options,
    facilityId,
  });
}

export async function saveUserPermissions(
  payload: SaveUserPermissionsPayload,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<SaveUserPermissionsResponse> {
  const baseUrl = options?.baseUrlOverride || API_BASE_URL || "";
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  const url = `${baseUrl}/api/v1/permissions`;

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const text = await response.text();
  const json = text ? (JSON.parse(text) as SaveUserPermissionsResponse) : null;

  if (!response.ok) {
    const message =
      (json as any)?.message ||
      (json as any)?.detail ||
      `Failed to save permissions (${response.status})`;
    throw new Error(message);
  }

  return json || {};
}

